import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  getProducts,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  bulkProductCreate,
} from "services";
import { handleAPIError, queryClient } from "utils";
import { ProductType } from "types";
import { toast } from "react-toastify";

// Fetch all products
export const useGetProducts = (id?: string | number) => {
  return useQuery({
    queryKey: [queryKeys.products, id],
    queryFn: async () => await getProducts(id),
  });
};

// Fetch a single product by ID
export const useGetProductById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.products, id], // Separate key for fetching single product
    queryFn: async () => await getProductById(id),
    enabled: !!id, // Only run query if ID is provided
  });
};

// Create a new product
export const useCreateProduct = () => {
  return useMutation({
    mutationFn: createProduct,
    onSuccess: async (data) => {
      toast.success("Product created successfully");

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.products],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Create a new product
export const useBulkProductCreate = () => {
  return useMutation({
    mutationFn: bulkProductCreate,
    onSuccess: async (data) => {
      toast.success("Upload successful");

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.products],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

type UpdateProductPayload = {
  id: string;
  data: ProductType;
};

// Update product hook
export const useUpdateProduct = (): UseMutationResult<
  any,
  Error,
  UpdateProductPayload
> => {
  return useMutation<any, Error, UpdateProductPayload>({
    mutationFn: ({ id, data }: UpdateProductPayload) => updateProduct(id, data),
    onSuccess: async () => {
      toast.success("Product info updated successfully");
      // Invalidate products query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.products],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a product
export const useDeleteProduct = () => {
  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: async () => {
      toast.success("Product deleted successfully");
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.products],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
